import { connectColors } from '@/theme/colors';
import { Autocomplete, Button, TextField } from '@mui/material';
import { useMemo } from 'react';

const FILTER_TEXT_SIZE = '11px';
const FILTER_CHIP_SIZE = '10px';

type FilterOption = {
  id: string;
  label: string;
};

type FilterDropdownProps = {
  options: FilterOption[];
  onChange: (selectedIds: string[]) => void;
  value: string[] | undefined;
  singleSelect?: boolean;
};

const FilterDropdown = ({
  options,
  onChange,
  value,
  singleSelect = false,
}: FilterDropdownProps) => {
  const selectedOptions = useMemo(() => {
    if (value && value.length > 0) {
      return options.filter((option) => value.includes(option.id));
    }
    return [];
  }, [value, options]);
  const cleanedUpOptions = options.filter(
    (option): option is FilterOption =>
      option.id !== undefined && option.label !== undefined,
  );

  const handleClear = () => {
    onChange([]);
  };

  // For single select, compute the value properly
  const autocompleteValue = useMemo(() => {
    if (singleSelect) {
      return selectedOptions.length > 0 ? selectedOptions[0] : null;
    }
    return selectedOptions;
  }, [singleSelect, selectedOptions]);

  return (
    <Autocomplete
      multiple={!singleSelect}
      limitTags={1}
      options={cleanedUpOptions}
      onChange={(_, newSelectedOptions) => {
        if (singleSelect) {
          const selected = newSelectedOptions as FilterOption | null;
          onChange(selected ? [selected.id] : []);
        } else {
          const selectedValues = (newSelectedOptions as FilterOption[]).map(
            (v) => v.id,
          );
          onChange(selectedValues);
        }
      }}
      value={autocompleteValue}
      disableClearable={!singleSelect}
      forcePopupIcon={false}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={selectedOptions.length === 0 ? 'Filter' : ''}
          size="small"
          sx={{ fontSize: FILTER_TEXT_SIZE }}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              sx: { fontSize: FILTER_TEXT_SIZE },
            },
          }}
        />
      )}
      slotProps={{
        listbox: {
          sx: { fontSize: FILTER_TEXT_SIZE, width: 'auto', minWidth: 120 },
          component: (props) => (
            <div {...props}>
              {selectedOptions.length > 0 && (
                <div
                  style={{
                    padding: '4px 16px',
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClear}
                    sx={{
                      padding: 0,
                      fontSize: FILTER_TEXT_SIZE,
                      color: connectColors.core.blue[900],
                      textTransform: 'none',
                    }}
                  >
                    Clear
                  </Button>
                </div>
              )}
              {props.children}
            </div>
          ),
        },
        chip: {
          size: 'small',
          sx: { fontSize: FILTER_CHIP_SIZE, height: '16px' },
        },
        popper: {
          sx: { width: 'auto !important', minWidth: 120 },
          placement: 'bottom-start',
        },
      }}
      getLimitTagsText={(more) => (
        <span style={{ fontSize: FILTER_CHIP_SIZE }}>+{more}</span>
      )}
      disableCloseOnSelect={!singleSelect}
      isOptionEqualToValue={(option, val) => {
        const o = (Array.isArray(option) ? option[0] : option) as FilterOption;
        const v = (Array.isArray(val) ? val[0] : val) as FilterOption;
        return o?.id === v?.id;
      }}
    />
  );
};
export default FilterDropdown;
